import {FormLabel, Grid} from '@mui/material';
import Barcode from 'react-barcode';
import {useTheme} from '@mui/material/styles';
import AgyleDictionaryField from '../../../ui/selector/agyleDictionaryField';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {barcodesDict} from '../../../../store/reducers/dicts';
import AgyleTextField from '../../../ui/fields/agyleTextField';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import makeStyles from '@mui/styles/makeStyles';
import RemoveIcon from '@mui/icons-material/Remove';
import AgyleSwitch from '../../../ui/switch/agyleSwitch';
import moment from 'moment';
import AgyleDatePicker from '../../../ui/fields/agyleDatePicker';
import AgyleButton from '../../../ui/buttons/agyleButton';
import API from '../../../../app/api';
import {user} from '../../../../store/reducers/user';
import Formatter from '../../../../util/formatter';
import _ from 'lodash';
import BarcodeUtil from '../../../../util/barcodeUtil';
import {setSnackbar} from '../../../../store/reducers/globals';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => ({
    barcodeRoot: {
      maxHeight: 340
    },
    iconBackground: {
        backgroundColor: theme.palette.background.dialogBorder,
        borderRadius: 6,
    },
    barcode: {
        display: 'flex',
        justifyContent: 'center',
    },
    divRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 5,
    },
    buttons: {
        justifyContent: 'flex-end',
        marginTop: 'auto'
    }
}));

const PatientBarcode = ({patient, gp, surgery, loadingData}) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();

    // Selectors
    const dict = useSelector(barcodesDict);
    const userObject = useSelector(user);

    const userFullName = Formatter.formatUserFullName(userObject);

    const [selectedBarCode, setSelectedBarCode] = useState(null);
    const [copies, setCopies] = useState(16);
    const [showDate, setShowDate] = useState(false);
    const [date, setDate] = useState(null);
    const [invalidDate, setInvalidDate] = useState(null);

    useEffect(() => {
        const findDefault = dict?.find((item) => item.main);
        if (findDefault) {
            setSelectedBarCode(findDefault);
        }
    }, [dict]);

    const handleChangeBarCode = (_e, val) => {
        setSelectedBarCode(val);
    };

    const handleChangeCopies = (e) => {
        const {value} = e.target;
        const val = parseInt(value);
        if (_.isNumber(val) && val > 0 && val < 100) {
            setCopies(val);
        } else {
            dispatch(setSnackbar({type: 'warning', message: t('Copies must be a number between 1 and 99')}));
        }
    };

    const handleSwitchShowDate = (e) => {
        const {checked} = e.target;
        setDate(checked ? moment() : null);
        setShowDate(checked);
    };

    const incrementCount = () => {
        if (copies < 99) {
            setCopies(copies + 1);
        }
    };

    const decrementCount = () => {
        if (copies > 1) {
            setCopies(copies - 1);
        }
    };

    const handlePrintBarcode = (single) => {
        const dateToSend = showDate ? moment(date) : moment();
        const ptObj = _.cloneDeep(patient?.patient);
        ptObj.gp = gp;
        ptObj.surgery = surgery;

        const body = {
            audit: {
                username: userFullName,
                printedDate: moment().format('YYYY-MM-DD HH:mm:ss'),
            },
            items: [{
                form: BarcodeUtil.prepareBarcodeFormObj(patient?.patient?.__id, dateToSend.format('DD-MM-YYYY')),
                patient: BarcodeUtil.preparePrintDataObj(ptObj),
            }],
            options: {
                numBarCodes: single ? 1 : copies,
                pagePerPatient: false,
                template: selectedBarCode?.__description,
            },
        };

        API.printBarcode(body)
            .then(res => {
                const blob = new Blob([res], {type: "application/pdf"});
                let objectUrl = URL.createObjectURL(blob);
                window.open(objectUrl, "_blank");
            })
            .catch(err => {
                dispatch(setSnackbar({type: 'error', message: t('An error occurred while generating the barcode')}));
                console.error(err);
            })
    };

    if (!patient?.patient) return null;

    return (
        <Grid container spacing={1} style={{padding: 1}} className={classes.barcodeRoot}>
            <Grid item xs={12} className={classes.barcode}>
                <Barcode
                    value={patient.patient.__id}
                    format="CODE128"
                    height={50}
                    background="transparent"
                    lineColor={theme.palette.inPatient.barCode}
                />
            </Grid>

            <Grid item xs={12}>
                <AgyleDictionaryField
                    label={t('Barcode')}
                    options={dict}
                    onChange={handleChangeBarCode}
                    value={selectedBarCode}
                    size="small"
                    noBorder={false}
                    withNewBackground
                    disableClearable
                />
            </Grid>

            <Grid item xs={12}>
                <FormLabel style={{fontSize: 'small'}}>
                    {t('Copies')}
                </FormLabel>

                <div className={classes.divRow}>
                    <div style={{width: 50}}>
                        <AgyleTextField
                            onChange={handleChangeCopies}
                            type="number"
                            value={copies + ''}
                            size="small"
                            noBorder={false}
                            withNewBackground
                        />
                    </div>

                    <IconButton
                        className={classes.iconBackground}
                        onClick={decrementCount}
                    >
                        <RemoveIcon/>
                    </IconButton>

                    <IconButton
                        className={classes.iconBackground}
                        onClick={incrementCount}
                    >
                        <AddIcon/>
                    </IconButton>
                </div>
            </Grid>

            <Grid item xs={12}>
                <AgyleSwitch
                    label={t('Date on form')}
                    onChange={handleSwitchShowDate}
                    value={showDate}
                />
            </Grid>

            {showDate &&
                <Grid item xs={12}>
                    <AgyleDatePicker
                        type="date"
                        value={date}
                        disablePast={false}
                        disableFuture={false}
                        onChange={setDate}
                        noBorder={false}
                        withNewBackground
                        size="small"
                        onError={setInvalidDate}
                    />
                </Grid>
            }
            <Grid item xs={12} className={clsx(classes.divRow, classes.buttons)}>
                <AgyleButton
                    onClick={() => handlePrintBarcode(false)}
                    colour="primary"
                    variant="outlined"
                    disabled={loadingData || !selectedBarCode || invalidDate}
                >
                    {t('Print Barcodes')}
                </AgyleButton>

                <AgyleButton
                    onClick={() => handlePrintBarcode(true)}
                    colour="primary"
                    variant="outlined"
                    disabled={loadingData || !selectedBarCode || invalidDate}
                >
                    {t('Print one')}
                </AgyleButton>
            </Grid>
        </Grid>
    );
};

export default React.memo(PatientBarcode);